import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  onboardingApi,
  type AdminOnboardingApplicationDto,
  OnboardingStatus,
  OnboardingStatusType,
} from '../api/onboarding';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { formatApplicationType } from '../utils/formatApplicationType';
import { FileText, Filter } from 'lucide-react';

const normalizeOnboardingStatus = (s: string | undefined) => (s ?? '').toUpperCase();

function OnboardingPage() {
  const [applications, setApplications] = useState<AdminOnboardingApplicationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listTab, setListTab] = useState<'pipeline' | 'archive'>('pipeline');
  const [selectedStatus, setSelectedStatus] = useState<OnboardingStatusType | ''>('');
  const [archiveStatusFilter, setArchiveStatusFilter] = useState<'' | typeof OnboardingStatus.Draft | typeof OnboardingStatus.Rejected>('');

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        const data = await onboardingApi.getByStatus(undefined);
        setApplications(Array.isArray(data) ? data : []);
        setError('');
      } catch (err: unknown) {
        console.error('Failed to fetch applications:', err);
        setError(err instanceof Error ? err.message : 'Failed to load applications');
      } finally {
        setLoading(false);
      }
    };

    void fetchApplications();
  }, []);

  const archiveCount = applications.filter((a) => {
    const st = normalizeOnboardingStatus(a.status);
    return st === OnboardingStatus.Draft || st === OnboardingStatus.Rejected;
  }).length;

  const filteredApplications = applications.filter((a) => {
    const st = normalizeOnboardingStatus(a.status);
    if (listTab === 'archive') {
      if (st !== OnboardingStatus.Draft && st !== OnboardingStatus.Rejected) return false;
      if (archiveStatusFilter && st !== archiveStatusFilter) return false;
      return true;
    }
    if (st === OnboardingStatus.Draft || st === OnboardingStatus.Rejected) return false;
    if (selectedStatus && st !== normalizeOnboardingStatus(selectedStatus)) return false;
    return true;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status.toUpperCase()) {
      case OnboardingStatus.Draft:
        return 'bg-gray-100 text-gray-800';
      case OnboardingStatus.Submitted:
        return 'bg-blue-100 text-blue-800';
      case OnboardingStatus.InReview:
        return 'bg-yellow-100 text-yellow-900';
      case OnboardingStatus.AdditionalInfoRequired:
        return 'bg-orange-100 text-orange-900';
      case OnboardingStatus.Approved:
        return 'bg-green-100 text-green-800';
      case OnboardingStatus.Rejected:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Onboarding Applications</h1>
          <p className="text-gray-600">Review and manage client onboarding applications</p>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Onboarding Applications</h1>
          <p className="text-gray-600">Review and manage client onboarding applications</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
          <p className="mt-2 text-sm text-gray-600">
            If nothing loads, verify the token and that the API returns <code className="rounded bg-gray-100 px-1">isSuccess</code>{' '}
            envelopes (same as portal).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Onboarding Applications</h1>
        <p className="text-gray-600">Review and manage client onboarding applications</p>
      </div>

      <div className="mb-4 border-b border-gray-200">
        <nav className="flex gap-1 -mb-px" aria-label="Onboarding lists">
          <button
            type="button"
            onClick={() => setListTab('pipeline')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors ${
              listTab === 'pipeline'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Review queue
          </button>
          <button
            type="button"
            onClick={() => setListTab('archive')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors inline-flex items-center gap-2 ${
              listTab === 'archive'
                ? 'bg-white border-gray-200 text-gray-900 z-10'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Draft &amp; rejected
            {archiveCount > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-800">
                {archiveCount}
              </span>
            ) : null}
          </button>
        </nav>
      </div>

      <div className="mb-6 bg-white shadow-sm rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">
              {listTab === 'pipeline' ? 'Filter by status:' : 'In this tab:'}
            </label>
          </div>
          {listTab === 'pipeline' ? (
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as OnboardingStatusType | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All statuses</option>
              <option value={OnboardingStatus.Submitted}>Submitted</option>
              <option value={OnboardingStatus.InReview}>In Review</option>
              <option value={OnboardingStatus.AdditionalInfoRequired}>Additional Info Required</option>
              <option value={OnboardingStatus.Approved}>Approved</option>
            </select>
          ) : (
            <select
              value={archiveStatusFilter}
              onChange={(e) =>
                setArchiveStatusFilter(
                  e.target.value as '' | typeof OnboardingStatus.Draft | typeof OnboardingStatus.Rejected
                )
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Draft and rejected</option>
              <option value={OnboardingStatus.Draft}>Draft only</option>
              <option value={OnboardingStatus.Rejected}>Rejected only</option>
            </select>
          )}
          <div className="ml-auto text-sm text-gray-500">
            Showing: {filteredApplications.length}
            <span className="text-gray-400"> · loaded {applications.length} total</span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Application
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Legal name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplications.map((application) => (
              <tr key={application.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 shrink-0" />
                    <span className="font-mono text-xs text-gray-600 max-w-[9rem] truncate" title={application.id}>
                      {application.id}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-[14rem]" title={(application.legalName ?? '').trim() || undefined}>
                  {(application.legalName ?? '').trim() ? (application.legalName ?? '').trim() : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatApplicationType(application.applicationType)}</td>
                <td
                  className="px-6 py-4 text-sm text-gray-900 font-mono text-xs font-semibold max-w-[10rem] truncate"
                  title={
                    [
                      formatPublicIdDisplay(application.userPublicId ?? undefined) || '—',
                      `internal clientId: ${application.clientId}`,
                      `internal userId: ${application.userId}`,
                    ].join('\n')
                  }
                >
                  {formatPublicIdDisplay(application.userPublicId ?? undefined) || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadgeClass(
                      application.status
                    )}`}
                  >
                    {getStatusLabel(application.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {application.submittedAt
                    ? new Date(application.submittedAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <Link to={`/onboarding/${application.id}`} className="text-blue-600 hover:text-blue-900">
                    Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No applications found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {listTab === 'archive'
                ? archiveStatusFilter
                  ? `No applications with status "${getStatusLabel(archiveStatusFilter)}"`
                  : 'No draft or rejected applications'
                : selectedStatus
                  ? `No applications with status "${getStatusLabel(selectedStatus)}" in the review queue`
                  : applications.length === 0
                    ? 'No onboarding applications available'
                    : 'No applications match the current filters'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingPage;
